export const periodLabels = (frequency) => {
  const f = String(frequency || "yearly");
  if (f === "monthly") {
    return [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
  }
  if (f === "quarterly") {
    return [
      "First Quarter",
      "Second Quarter",
      "Third Quarter",
      "Fourth Quarter",
    ];
  }
  return ["Yearly Emission"];
};

export const ensureArraySize = (arr, size) => {
  const out = (Array.isArray(arr) ? arr : []).slice(0, size);
  while (out.length < size) out.push("");
  return out;
};

export const ensureFileArraySize = (arr, size) => {
  const out = (Array.isArray(arr) ? arr : []).slice(0, size);
  while (out.length < size) out.push(null);
  return out;
};

const emptyToUndefined = (value) =>
  value === "" || typeof value === "undefined" ? undefined : value;

const numberOrUndefined = (value) => {
  if (value === "" || value === null || typeof value === "undefined")
    return undefined;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

const toNumOrNull = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);

const pruneEmpty = (value) => {
  if (typeof value === "undefined" || value === null) return undefined;
  if (typeof File !== "undefined" && value instanceof File) return value;
  if (Array.isArray(value)) {
    const items = value
      .map(pruneEmpty)
      .filter(
        (item) =>
          item !== undefined &&
          !(typeof item === "object" && Object.keys(item).length === 0),
      );
    return items.length > 0 ? items : undefined;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).reduce((acc, [key, val]) => {
      const cleaned = pruneEmpty(val);
      if (cleaned !== undefined) acc[key] = cleaned;
      return acc;
    }, {});
    return Object.keys(entries).length > 0 ? entries : undefined;
  }
  if (value === "") return undefined;
  return value;
};

const buildScopeEmission = (
  key,
  freq,
  emissions,
  refFiles,
  refFileName,
  refFileLink,
) => {
  const f = freq[key];
  const labels = periodLabels(f);
  const values = ensureArraySize(emissions[key] || [""], labels.length);
  const files = ensureFileArraySize(refFiles[key] || [null], labels.length);
  const fileNames = ensureArraySize(refFileName[key] || [""], labels.length);
  const fileLinks = ensureArraySize(refFileLink[key] || [""], labels.length);

  return {
    frequence: f,
    data: labels.map((name, i) => {
      const val = toNumOrNull(values[i]);
      const fileName = fileNames[i];
      const fileLink = fileLinks[i];
      const file = files[i];
      const base = { name, file, refFileName: file ? file.name || "" : "" };
      return val === null
        ? base
        : {
            ...base,
            emission: val,
            refFileName: fileName,
            refFileLink: fileLink,
          };
    }),
  };
};

export const buildNxplanPayload = ({
  form,
  freq,
  emissions,
  baseline,
  refFiles,
  refFileName,
  refFileLink,
  progress,
}) => {
  const rawPayload = {
    project: "nxplan",
    progress,
    organizationBoundary: emptyToUndefined(form.organizationBoundary),
    baseYear: numberOrUndefined(form.baseYear),
    reportingYear: numberOrUndefined(form.reportingYear),
    targetYear: numberOrUndefined(form.targetYear),
    isSBTi: emptyToUndefined(form.isSBTi),
    sbtiTemperature:
      form.isSBTi === "yes"
        ? numberOrUndefined(form.sbtiTemperature)
        : undefined,
    targetType:
      form.isSBTi === "yes" ? emptyToUndefined(form.targetType) : undefined,
    regressionModel:
      form.isSBTi === "yes"
        ? emptyToUndefined(form.regressionModel)
        : undefined,
    targetReduction:
      form.isSBTi === "no"
        ? numberOrUndefined(form.targetReduction)
        : undefined,
    baselineValue: {
      scope1: toNumOrNull(baseline.scope1),
      scope2: toNumOrNull(baseline.scope2),
      scope3: toNumOrNull(baseline.scope3),
    },
    scope1Emission: buildScopeEmission(
      "scope1",
      freq,
      emissions,
      refFiles,
      refFileName,
      refFileLink,
    ),
    scope2Emission: buildScopeEmission(
      "scope2",
      freq,
      emissions,
      refFiles,
      refFileName,
      refFileLink,
    ),
    scope3Emission: buildScopeEmission(
      "scope3",
      freq,
      emissions,
      refFiles,
      refFileName,
      refFileLink,
    ),
  };

  const payload = pruneEmpty(rawPayload) || { project: "nxplan" };
  payload.project = "nxplan";
  return payload;
};

export const formatCompactNumber = (value) => {
  const number = Number(value) || 0;
  if (Math.abs(number) < 1000) return number.toFixed(0);
  return new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(number);
};

export function normalizeNxplanDataForProgress(
  data = {},
  baseYear,
  organizationBoundary,
  reportingYear,
) {
  const form = {
    organizationBoundary:
      data.organizationBoundary ??
      data.organizationBoundary ??
      organizationBoundary ??
      "",
    baseYear: data.baseYear ?? baseYear ?? "",
    reportingYear: data.reportingYear ?? reportingYear ?? "",
    targetYear: data.targetYear ?? "",
    isSBTi: data.isSBTi ?? data.sbtiTarget ?? "",
    sbtiTemperature: data.sbtiTemperature ?? "",
    targetType: data.targetType ?? data.targetReductionType ?? "",
    regressionModel: data.regressionModel ?? data.absoluteModel ?? "",
    targetReduction: data.targetReduction ?? "",
  };
  const freq = {
    scope1:
      data.scope1Emission?.frequence ||
      data.scope1Emission?.frequency ||
      "yearly",
    scope2:
      data.scope2Emission?.frequence ||
      data.scope2Emission?.frequency ||
      "yearly",
    scope3:
      data.scope3Emission?.frequence ||
      data.scope3Emission?.frequency ||
      "yearly",
  };
  const emissions = {
    scope1: data.scope1Emission?.data || [],
    scope2: data.scope2Emission?.data || [],
    scope3: data.scope3Emission?.data || [],
  };
  const baseline = {
    scope1: data?.baselineValue?.scope1 ?? "",
    scope2: data?.baselineValue?.scope2 ?? "",
    scope3: data?.baselineValue?.scope3 ?? "",
  };
  return { form, freq, emissions, baseline };
}
